// UI slice: theme, sound preference, workspace disclosure pole.
// One owner for interface-level state (docs/ARCHITECTURE.md ownership table).

import { create } from "zustand";
import { setSuperProp } from "@/analytics/service";
import { setUiSoundEnabled, uiSoundEnabled } from "@/sounds/ui";

export type Theme = "dark" | "light";
/** The progressive-disclosure dial: 0 = Flow rail, 1 = full Studio. */
export type WorkspacePole = "flow" | "studio";

interface UiState {
  theme: Theme;
  uiSound: boolean;
  pole: WorkspacePole;
  setTheme: (t: Theme) => void;
  setUiSound: (on: boolean) => void;
  setPole: (p: WorkspacePole) => void;
}

function storedTheme(): Theme {
  try {
    const t = localStorage.getItem("bd.theme");
    return t === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

function storedPole(): WorkspacePole {
  try {
    return localStorage.getItem("bd.pole") === "studio" ? "studio" : "flow";
  } catch {
    return "flow";
  }
}

export const useUi = create<UiState>((set) => ({
  theme: storedTheme(),
  uiSound: uiSoundEnabled(),
  pole: storedPole(),
  setTheme: (theme) => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem("bd.theme", theme);
    } catch {
      /* storage unavailable */
    }
    setSuperProp("theme", theme);
    set({ theme });
  },
  setUiSound: (uiSound) => {
    setUiSoundEnabled(uiSound);
    set({ uiSound });
  },
  setPole: (pole) => {
    try {
      localStorage.setItem("bd.pole", pole);
    } catch {
      /* storage unavailable */
    }
    setSuperProp("workspacePole", pole);
    set({ pole });
  },
}));
